import { List as ImList, Map as MapImmutable } from 'immutable';
import { DateTime } from 'luxon';
import { LruObjectCache, createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';

import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { tagsSelectors } from 'app-components/flux/tags';
import { transactionsUtils } from 'app-components/flux/transactions';
import type RootState from 'app-components/utils/redux-store/rootState';

import { SCHEDULED_TRANSACTIONS_REDUCER_KEY } from './scheduledTransactionsTypes';
import { frequencyAliasFromRecurrence } from './scheduledTransactionsUtils';

type State = Pick<RootState, typeof SCHEDULED_TRANSACTIONS_REDUCER_KEY>;

export const getScheduledTransactionsStore = (state: State) => state[SCHEDULED_TRANSACTIONS_REDUCER_KEY];

export const getResourcesById = (state: State) => getScheduledTransactionsStore(state).resourcesById;
export const getLoadPending = (state: State) => getScheduledTransactionsStore(state).loadPending;
export const getError = (state: State) => getScheduledTransactionsStore(state).error;
export const getIsLoading = (state: State) => getScheduledTransactionsStore(state).isLoading;
export const getLastSyncDate = (state: State) => getScheduledTransactionsStore(state).lastSyncDate;

export const getScheduledTransactions = getResourcesById;
export const getScheduledTransactionById = (state: State, id: string) => getScheduledTransactions(state).get(id);
export const getScheduledTransactionByClientId = (state: State, clientId: string) =>
  getScheduledTransactions(state).find((scheduledTransaction) => scheduledTransaction.clientId === clientId);

export const getScheduledTransactionsSortedByPayee = createSelector(
  getScheduledTransactions,
  (scheduledTransactions) =>
    !scheduledTransactions
      ? null
      : scheduledTransactions.sort(
          (a, b) =>
            a.transaction?.payee?.localeCompare(b.transaction?.payee ?? '', undefined, {
              numeric: true,
              sensitivity: 'base',
            }) ?? 0,
        ),
);

export const getUserVerifiedScheduledTransactions = createSelector(
  getScheduledTransactions,
  (scheduledTransactionsById) =>
    scheduledTransactionsById.filter(
      (scheduledTransaction) =>
        scheduledTransaction.isUserVerified === undefined || scheduledTransaction.isUserVerified,
    ),
);

export const getNonVerifiedScheduledTransactions = createSelector(
  getScheduledTransactions,
  (scheduledTransactionsById) =>
    scheduledTransactionsById.filter(
      (scheduledTransaction) => scheduledTransaction.isUserVerified === false || !scheduledTransaction.isUserVerified,
    ),
);

export const existsScheduledTransactionWithTag = createSelector(
  getUserVerifiedScheduledTransactions,
  (_: unknown, { id }: { id?: string }) => id,
  (state, tagId) =>
    tagId
      ? Boolean(
          state.find((stxn) =>
            stxn.transaction ? Boolean(transactionsUtils.txnHasTag(stxn.transaction, tagId)) : false,
          ),
        )
      : false,
);

export const getBillsAndIncome = createSelector(
  getScheduledTransactions,
  categoriesSelectors.getIncomeCOAIds,
  (scheduledTransactions, incomeCOAs) => ({
    bills: scheduledTransactions
      .filter((model) => !transactionsUtils.isIncomeFromTxn(model.transaction, incomeCOAs))
      .toOrderedSet(),
    income: scheduledTransactions
      .filter((model) => transactionsUtils.isIncomeFromTxn(model.transaction, incomeCOAs))
      .toOrderedSet(),
  }),
);
export const getBillsAndIncomeUserVerified = createSelector(
  getUserVerifiedScheduledTransactions,
  categoriesSelectors.getIncomeCOAIds,
  (scheduledTransactions, incomeCOAs) =>
    MapImmutable({
      bills: scheduledTransactions
        .filter((model) => !transactionsUtils.isIncomeFromTxn(model.transaction, incomeCOAs))
        .toOrderedSet(),
      income: scheduledTransactions
        .filter((model) => transactionsUtils.isIncomeFromTxn(model.transaction, incomeCOAs))
        .toOrderedSet(),
    }),
);

export const getAccountFilteredBillsAndIncome = createSelector(
  getScheduledTransactions,
  categoriesSelectors.getIncomeCOAIds,
  (_state: unknown, accountIds: string[]) => accountIds, // array of accountIds to filter by
  (scheduledTransactions, incomeCOAs, accountIds) => {
    const accountSchedules = scheduledTransactions.filter((model) =>
      accountIds.includes(model.transaction?.accountId ?? ''),
    );
    return {
      bills: accountSchedules
        .filter((model) => !transactionsUtils.isIncomeFromTxn(model.transaction, incomeCOAs))
        .toOrderedSet(),
      income: accountSchedules
        .filter((model) => transactionsUtils.isIncomeFromTxn(model.transaction, incomeCOAs))
        .toOrderedSet(),
    };
  },
);

export const getUnconnectedBills = createSelector(
  getUserVerifiedScheduledTransactions,
  categoriesSelectors.getIncomeCOAIds,
  (scheduledTransactionsById, incomeCOAs) =>
    scheduledTransactionsById.filter(
      (model) =>
        (!model.billPresentmentAccountId || model.billPresentmentAccountId === '0') &&
        !incomeCOAs.has(model?.transaction?.coa?.id ?? ''),
    ),
);

export const getScheduledTransactionForBillerAccountId = (state: any, accountId: string) =>
  getScheduledTransactions(state).find(
    (scheduledTransaction) => scheduledTransaction.billPresentmentAccountId === accountId,
  );

export const modelTypes = Object.freeze({
  ALL: 'All types',
  INCOME: 'Income',
  BILLS: 'Bills',
  TRANSFERS: 'Transfers',
});

export const typeFilteredScheduledTransactions = createSelector(
  getUserVerifiedScheduledTransactions,
  (_state: unknown, type: string) => type,
  categoriesSelectors.getIncomeCOAIds,
  (scheduledTransactions, type, incomeCOAIds) => {
    let filteredSchedules = scheduledTransactions;
    if (type) {
      // filter stuff
      if (type === modelTypes.INCOME) {
        // only show standard income
        filteredSchedules = scheduledTransactions.filter((model) =>
          transactionsUtils.isIncomeFromTxn(model?.transaction, incomeCOAIds),
        );
      } else if (type === modelTypes.TRANSFERS) {
        // connected transfer
        // show loose transfers (including uncategorized and balance adjustments)
        filteredSchedules = scheduledTransactions.filter((model) =>
          transactionsUtils.isGeneralTransferTxn(model?.transaction),
        );
      } else if (type === modelTypes.BILLS) {
        // don't show income
        filteredSchedules = scheduledTransactions.filter(
          (model) => !transactionsUtils.isIncomeFromTxn(model?.transaction, incomeCOAIds),
        );
      } else {
        // show everything
        filteredSchedules = scheduledTransactions;
      }
    }
    return filteredSchedules;
  },
);

export const bucketizeTransactionsByAccountId = createCachedSelector(
  (transactionsById: MapImmutable<string, { accountId: string }>) => transactionsById,
  (transactionsById) =>
    MapImmutable<string, ImList<{ accountId: string }>>().withMutations(
      (transactionsByAccountIdMutable) =>
        transactionsById &&
        transactionsById.forEach((transaction) =>
          transactionsByAccountIdMutable.set(
            transaction.accountId,
            (transactionsByAccountIdMutable.get(transaction.accountId) || ImList()).push(transaction),
          ),
        ),
    ),
)({
  keySelector: (transactionsById) => transactionsById?.hashCode() ?? 'none',
  cacheObject: new LruObjectCache({ cacheSize: 6 }),
});

// Used in all recurring search for better keyword mapping.
export const schTxnsSearchNames = createSelector(
  getUserVerifiedScheduledTransactions,
  accountsSelectors.getAccountsById,
  categoriesSelectors.getCategoriesById,
  tagsSelectors.getTags,
  (schTxns, accountsById, categoriesById, tagsById) =>
    schTxns.map((schTxn) => ({
      payeeName: schTxn.transaction?.payee || 'New Series',
      amount: String(schTxn.transaction?.amount) ?? '',
      accountName: accountsById?.get(schTxn.transaction?.accountId ?? '')?.name || '',
      frequency: frequencyAliasFromRecurrence(schTxn.recurrence) || '',
      nextDate: (schTxn.dueOn && DateTime.fromISO(schTxn.dueOn)?.toLocaleString()) || '',
      type: schTxn.type || '',
      category: categoriesById?.get(schTxn.transaction?.coa?.id ?? '')?.name || '',
      tags: schTxn.transaction?.tags?.map((tag) => tagsById?.get(tag?.id || '')?.name)?.join?.(' ') || '',
    })),
);
